import React, { useState, useEffect, useCallback } from 'react';
import realTimeDeviceService from '../../services/realTimeDeviceService';
import usePagination from '../utils/usePagination';
import { formatLocalDateTime } from '../utils/formatDateTime';
import ConfirmationDialog from '../popups/ConfirmationDialog';
import '../assets/AllTabsCommon.css';
import '../assets/Home.css';
import '../assets/RemoteDebugSessionTabs.css';

const ROWS_PER_PAGE = 10;

const SCHEDULED_COLUMNS = [
  { key: 'sessionId', label: 'Session ID' },
  { key: 'systemId', label: 'System ID' },
  { key: 'status', label: 'Status' },
  { key: 'scheduledTime', label: 'Scheduled Time' },
  { key: 'duration', label: 'Duration' },
  { key: 'schedulingMode', label: 'Mode' },
  { key: 'actionCount', label: 'Actions' },
  { key: 'createdUser', label: 'Created By' },
  { key: 'cancel', label: '' },
];

/**
 * Real-Time Device Management — Scheduled sessions.
 */
function RTScheduledSessions({ onScheduleSession }) {
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [cancelTarget, setCancelTarget] = useState(null);

  const load = useCallback(async () => {
    const data = await realTimeDeviceService.getScheduledSessions();
    setSessions(Array.isArray(data) ? data : []);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async () => {
    const id = cancelTarget?.scheduledSessionId || cancelTarget?.sessionId;
    if (!id) return;
    try {
      await realTimeDeviceService.cancelScheduledSession(id);
      setCancelTarget(null);
      await load();
    } catch (err) {
      setCancelTarget(null);
    }
  };

  const filtered = sessions.filter((s) =>
    (s.systemId || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  const { pageItems, renderPageNumbers } = usePagination(filtered, currentPage, setCurrentPage, ROWS_PER_PAGE);

  const renderCell = (session, colKey) => {
    switch (colKey) {
      case 'status':
        return <span className="rt-scheduled-badge">📅 {session.status}</span>;
      case 'scheduledTime':
        return formatLocalDateTime(session.scheduledTime);
      case 'duration':
        return session.durationMins ? `${session.durationMins} min` : '—';
      case 'schedulingMode':
        return session.schedulingMode || '—';
      case 'createdUser':
        return session.createdUser || '—';
      case 'cancel':
        return (
          <button
            className="go-back-button"
            style={{ marginTop: 0, padding: '4px 10px', fontSize: 12 }}
            onClick={() => setCancelTarget(session)}
          >
            Cancel
          </button>
        );
      default:
        return session[colKey];
    }
  };

  return (
    <div className="data-table-tab-container">
      <div className="rt-page-header" style={{ alignItems: 'center', marginBottom: 16 }}>
        <div className="search-container" style={{ margin: 0, flex: 1, maxWidth: 320 }}>
          <input
            type="text"
            placeholder="Search by System ID"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
          />
        </div>
        <div className="rt-page-header-actions">
          <button className="submit-button" style={{ marginTop: 0 }} onClick={onScheduleSession}>+ Schedule Session</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card rt-card">
          <div className="rt-empty-state">
            <span className="rt-empty-icon">📅</span>
            <p className="rt-empty-title">No Scheduled Sessions</p>
            <p className="rt-empty-hint">Use the Start tab to schedule a session for a specific date and time.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>{SCHEDULED_COLUMNS.map((col) => <th key={col.key}>{col.label}</th>)}</tr>
              </thead>
              <tbody>
                {pageItems.map((session) => (
                  <tr key={session.sessionId}>
                    {SCHEDULED_COLUMNS.map((col) => <td key={col.key}>{renderCell(session, col.key)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-controls">{renderPageNumbers()}</div>
        </>
      )}

      {cancelTarget && (
        <ConfirmationDialog
          message={`Cancel the scheduled session for device ${cancelTarget.systemId}?`}
          onConfirm={handleCancel}
          onCancel={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
}

export default RTScheduledSessions;
